import { useState } from "react";
import CnpjUtils from "../../../shared/utils/CnpjUtils";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";

export function useFinancialIndicatedConditions() {
	const [indicatedConditions, setIndicatedConditions] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const normalizePaymentConditionData = condicoesIndicadas => {
        return Array.isArray(condicoesIndicadas)
            ? condicoesIndicadas
            : condicoesIndicadas
                ? [condicoesIndicadas]
                : [];
    };

    const normalizePaymentConditions = condicoesIndicadas => {
        return normalizePaymentConditionData(condicoesIndicadas).map(condition => ({
            label: condition.nome,
            value: String(condition.id)
        }));
    };

    const getFirstPaymentCondition = condicoesIndicadas => {
        if (Array.isArray(condicoesIndicadas)) {
            return condicoesIndicadas[0] || null;
        }

        return condicoesIndicadas || null;
    };

    const formatFinancialIndicatedConditions = (
        indicatedConditionsData,
        selectedInstallments
    ) => {
        const minimumInitialPercentual =
            indicatedConditionsData?.percentualEntradaMinimoIndicada ??
            indicatedConditionsData?.["percentual-entrada-minimo-indicada"] ??
            "";

        const maximumInitialPercentual =
            indicatedConditionsData?.percentualEntradaMaximoIndicada ??
            indicatedConditionsData?.["percentual-entrada-maximo-indicada"] ??
            "";

        const indicatedPaymentConditions =
            indicatedConditionsData?.condicoesIndicadas ??
            indicatedConditionsData?.["condicoes-indicadas"] ??
            [];

		const parcelaReferencia =
			indicatedConditionsData?.parcelaReferencia ??
			indicatedConditionsData?.parcela_referencia ??
			indicatedConditionsData?.["parcela-referencia"] ??
			"";
		
        const paymentConditionData = normalizePaymentConditionData(indicatedPaymentConditions)
			.map(condition => ({
				...condition,
				parcelaReferencia: condition?.parcelaReferencia ??
					condition?.parcela_referencia ??
					condition?.["parcela-referencia"] ??
					parcelaReferencia
			}));

        const paymentConditionOptions = normalizePaymentConditions(indicatedPaymentConditions);

		return {
			minimumInitialPercentual,
			maximumInitialPercentual,
			paymentConditions: "",
			paymentConditionOptions,
			paymentConditionData,
			installments: String(selectedInstallments),
			downPaymentPercentage: ""
		};
    };

	async function fetchFinancialIndicatedConditions(
		equipmentValue,
		installments,
		clientDocument
	) {
		const cleanClientDocument = CnpjUtils.cleanAlphanumericCnpj(clientDocument || "");

		if (!equipmentValue || !installments || !cleanClientDocument) {
			const message = "Informe o CNPJ, o valor do equipamento e a quantidade de parcelas.";

			setError(message);

			return {
				ok: false,
				error: message
			};
		}

		try {
			setLoading(true);
			setError(null);

			const {
				accessToken,
				clientId
			} = getAuthParams();

			const params = new URLSearchParams();

			params.append("cnpjCliente", cleanClientDocument);
			params.append("valorEquipamento", equipmentValue);
			params.append("numeroParcelas", installments);

			const requestOptions = {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					"access_token": accessToken,
					"client_id": clientId
				}
			};

			const response = await fetch(
				process.env.REACT_APP_ESSILOR_API +
					`pacto-rebates-simulador/v1/financeiro/condicoes-indicadas?${params.toString()}`,
				requestOptions
			);

			if (!response.ok) {
				await throwResponseError(
					response,
					"Não foi possível carregar as condições indicadas."
				);
			}

			const data = await response.json();

			const indicatedConditionsData = data?.resultado ?? data;

			const indicatedPaymentConditions =
				indicatedConditionsData?.condicoesIndicadas ??
				indicatedConditionsData?.["condicoes-indicadas"] ??
				[];

			const hasEmptyIndicatedConditions =
				Array.isArray(indicatedConditionsData) && indicatedConditionsData.length === 0;

			const hasEmptyPaymentConditions =
				Array.isArray(indicatedPaymentConditions) &&
				indicatedPaymentConditions.length === 0;

			if (hasEmptyIndicatedConditions || hasEmptyPaymentConditions) {
				throw new Error(
					"Não foram encontradas condições de pagamento para os dados informados."
				);
			}

			const formattedIndicatedConditions = formatFinancialIndicatedConditions(
				indicatedConditionsData,
				installments
			);

			setIndicatedConditions(formattedIndicatedConditions);

			return {
				ok: true,
				indicatedConditions: formattedIndicatedConditions
			};
		} catch (requestError) {
			setIndicatedConditions(null);
			setError(requestError.message);

			return {
				ok: false,
				error: requestError.message
			};
		} finally {
			setLoading(false);
		}
	}

	return {
		indicatedConditions,
		loading,
		error,
		fetchFinancialIndicatedConditions
	};
}
